import { useRouterState } from '@tanstack/react-router';
import { FlaskConical, Globe } from 'lucide-react';

import { t } from '@/shared/i18n';
import { cx } from '@/shared/lib/cx';
import { Tooltip } from '@/shared/ui/Tooltip';

import { environmentFor, ENV_KEYS, type EnvKey } from '../site/scope';
import type { SiteScope } from '../site/useSiteScope';

import { targetPath } from './navigation';
import { RouterAnchor } from './RouterAnchor';
import styles from './EnvSwitcher.module.scss';

const labels: Record<EnvKey, string> = {
  prod: t.env.production,
  dev: t.env.development,
};

const icons: Record<EnvKey, React.ReactNode> = {
  prod: <Globe />,
  dev: <FlaskConical />,
};

/**
 * Переключатель окружения: Production и Development, всегда на виду.
 *
 * Сегменты, а не выпадающий список: окружений ровно два, и оба должны быть
 * видны сразу — где ты сейчас и куда можно уйти. Переход сохраняет раздел:
 * с «Товаров» на проде — в «Товары» на деве. Кроме записи: на другом
 * окружении это другая запись, поэтому переход ведёт на главную сайта.
 *
 * В свёрнутой колонке остаётся одна иконка текущего окружения.
 */
export function EnvSwitcher({ scope, compact }: { scope: SiteScope; compact: boolean }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (compact) {
    return (
      <Tooltip content={labels[scope.env]} side="right">
        <span
          className={cx(styles['badge'], scope.env === 'dev' && styles['badge--dev'])}
          aria-label={`${t.env.switcher}: ${labels[scope.env]}`}
        >
          {icons[scope.env]}
        </span>
      </Tooltip>
    );
  }

  return (
    <div className={styles['switcher']} role="group" aria-label={t.env.switcher}>
      {/* С сужением «только Development» прода для человека нет вовсе —
          ни сегмента, ни намёка, что туда можно уйти. */}
      {ENV_KEYS.filter((key) => !(scope.devOnly && key === 'prod')).map((key) => {
        const current = key === scope.env;
        const exists = environmentFor(scope.environments, key) !== undefined;
        // Пока окружения не пришли, не утверждаем, что какого-то нет.
        const missing = !exists && !scope.loading;

        const className = cx(
          styles['segment'],
          current && styles['segment--current'],
          current && key === 'dev' && styles['segment--dev'],
        );

        if (missing && !current) {
          return (
            <Tooltip key={key} content={t.env.missingDevelopment}>
              <span className={cx(className, styles['segment--missing'])} aria-disabled="true">
                {icons[key]}
                {labels[key]}
              </span>
            </Tooltip>
          );
        }

        const href = targetPath(pathname, scope.siteId, scope.env, key);

        // Ссылка, а не кнопка: окружение можно открыть в соседней вкладке и
        // сравнить с текущим.
        return (
          <RouterAnchor
            key={key}
            href={href}
            className={className}
            aria-current={current ? 'page' : undefined}
          >
            {icons[key]}
            {labels[key]}
          </RouterAnchor>
        );
      })}
    </div>
  );
}
